import { useState, useEffect, useRef } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";
import { authFetch } from "../lib/authFetch";
import {
  PORTAIL_TICKET_TITLE_PLACEHOLDER,
  normalizeTicketTitle,
  validateTicketFields,
  type TicketFieldErrors,
} from "../lib/ticket-validation";
import {
  LifeBuoy, Plus, Loader2, AlertCircle, RefreshCw,
  Calendar, User, Link2, Copy, Check, Bot, Search, X, LayoutGrid, List,
} from "lucide-react";

interface PortailTicket {
  id: string;
  ticketNumber: string;
  titre: string | null;
  status: string;
  priority: string;
  contactName: string | null;
  contactEmail: string | null;
  problemDescription: string | null;
  serialNumberFree: string | null;
  datePanne: string | null;
  livraisonNomEntreprise: string | null;
  livraisonAdresse: string | null;
  livraisonContact: string | null;
  livraisonPhone: string | null;
  videoUrl: string | null;
  createdAt: string;
}

type TicketViewMode = "cards" | "list";

const STATUS_LABELS: Record<string, string> = {
  nouveau: "Nouveau",
  en_cours: "En cours",
  en_attente_piece: "En attente pièce",
  resolu: "Résolu",
  ferme: "Fermé",
};

const STATUS_COLORS: Record<string, string> = {
  nouveau: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  en_cours: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  en_attente_piece: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  resolu: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  ferme: "bg-secondary text-muted-foreground",
};

const PRIORITY_LABELS: Record<string, string> = {
  faible: "Faible", moyen: "Normal", eleve: "Élevé", urgent: "Urgent",
};
const PRIORITY_COLORS: Record<string, string> = {
  faible: "bg-secondary text-muted-foreground",
  moyen: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  eleve: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  urgent: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

function formatDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

interface CreateTicketForm {
  titre: string;
  problemDescription: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  serialNumberFree: string;
  datePanne: string;
  clientFinalNom: string;
  livraisonNomEntreprise: string;
  livraisonAdresse: string;
  livraisonContact: string;
  livraisonPhone: string;
  video: File | null;
}

const EMPTY_FORM: CreateTicketForm = {
  titre: "", problemDescription: "", contactName: "", contactPhone: "",
  contactEmail: "", serialNumberFree: "", datePanne: "",
  clientFinalNom: "",
  livraisonNomEntreprise: "", livraisonAdresse: "",
  livraisonContact: "", livraisonPhone: "", video: null,
};

export default function TicketsPage() {
  const { portailUser, brand } = usePortailAuth();
  const { navigate } = usePortailRouter();
  const primary = brand?.couleurPrimaire || "#2563eb";

  const [tickets, setTickets] = useState<PortailTicket[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<TicketViewMode>(() => {
    try {
      const savedMode = window.localStorage.getItem("portailTicketsViewMode");
      return savedMode === "cards" || savedMode === "list" ? savedMode : "cards";
    } catch {
      return "cards";
    }
  });
  const allTicketsRef = useRef<PortailTicket[] | null>(null);
  const requestSequenceRef = useRef(0);

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateTicketForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<TicketFieldErrors>({});
  const submitLockRef = useRef(false);

  // Contexte robot transmis depuis « Robots actifs » via history.state
  const [ticketRobotContext, setTicketRobotContext] = useState<{ sn: string; clientFinal: string; modele: string } | null>(null);

  useEffect(() => {
    const state = window.history.state as { openTicketFor?: { sn: string; clientFinal: string; modele: string } } | null;
    if (state?.openTicketFor) {
      const ctx = state.openTicketFor;
      // Consommer le state pour éviter la réouverture au refresh/retour
      window.history.replaceState(null, "", "/tickets");
      setTicketRobotContext(ctx);
      const prenom = portailUser?.prenom ?? "";
      const nom = portailUser?.nom ?? "";
      const contactName = [prenom, nom].filter(Boolean).join(" ");
      setForm({
        ...EMPTY_FORM,
        serialNumberFree: ctx.sn,
        clientFinalNom: ctx.clientFinal || "",
        contactName,
        contactEmail: portailUser?.email ?? "",
        contactPhone: portailUser?.telephone ?? "",
      });
      setShowCreate(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [shareToken, setShareToken] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShare, setShowShare] = useState(false);

  async function loadTickets(search = "", requestId = ++requestSequenceRef.current) {
    setLoading(true);
    setError(null);
    try {
      const normalizedSearch = search.trim().slice(0, 100);
      const url = normalizedSearch.length >= 2
        ? `/api/portail/tickets?q=${encodeURIComponent(normalizedSearch)}`
        : "/api/portail/tickets";
      const response = await authFetch(url);
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || "Impossible de charger les tickets.");
      }
      const data = await response.json();
      if (requestId !== requestSequenceRef.current) return;
      const nextTickets = Array.isArray(data) ? data : [];
      if (normalizedSearch.length < 2) {
        allTicketsRef.current = nextTickets;
      }
      setTickets(nextTickets);
    } catch {
      if (requestId !== requestSequenceRef.current) return;
      setError("Erreur lors du chargement des tickets.");
    } finally {
      if (requestId === requestSequenceRef.current) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    const normalizedSearch = searchInput.trim().slice(0, 100);
    const requestId = ++requestSequenceRef.current;

    if (normalizedSearch.length < 2) {
      setSearchQuery("");
      setLoading(false);
      setError(null);
      if (allTicketsRef.current) {
        setTickets(allTicketsRef.current);
      } else {
        void loadTickets("", requestId);
      }
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setSearchQuery(normalizedSearch);
      void loadTickets(normalizedSearch, requestId);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  // loadTickets is intentionally a local request helper; searchInput is the trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  function clearSearch() {
    requestSequenceRef.current += 1;
    setSearchInput("");
    setSearchQuery("");
    setStatusFilter("all");
    setLoading(false);
    setError(null);
    if (allTicketsRef.current) {
      setTickets(allTicketsRef.current);
    }
  }

  function switchView(mode: TicketViewMode) {
    setViewMode(mode);
    try {
      window.localStorage.setItem("portailTicketsViewMode", mode);
    } catch {
      // Le mode reste fonctionnel même si le stockage local est indisponible.
    }
  }

  async function loadShareToken() {
    setLoadingToken(true);
    try {
      const data = await authFetch('/api/portail/share-token').then(r => r.json());
      setShareToken(data.token);
    } catch { /* ignore */ } finally {
      setLoadingToken(false);
    }
  }

  async function regenerateToken() {
    setLoadingToken(true);
    try {
      const data = await authFetch('/api/portail/share-token/regenerate', { method: 'POST' }).then(r => r.json());
      setShareToken(data.token);
    } catch { /* ignore */ } finally {
      setLoadingToken(false);
    }
  }

  function openShare() {
    setShowShare(true);
    if (!shareToken) loadShareToken();
  }

  function getPublicUrl() {
    if (!shareToken) return "";
    const base = window.location.origin;
    return `${base}/ticket-public?token=${shareToken}`;
  }

  function copyUrl() {
    navigator.clipboard.writeText(getPublicUrl());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const validationErrors = validateTicketFields(form.titre, form.problemDescription);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setCreateError(null);
      return;
    }
    if (!form.contactName.trim()) { setCreateError("Nom du contact requis."); return; }
    if (!form.contactEmail.trim()) { setCreateError("Email requis."); return; }
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
    setCreateError(null);
    try {
      const payload = {
        ...form,
        titre: normalizeTicketTitle(form.titre),
        problemDescription: form.problemDescription.trim(),
      };
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (k === 'video') {
          if (v) fd.append('video', v as File);
        } else {
          fd.append(k, v as string);
        }
      });
      const res = await authFetch('/api/portail/tickets', { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json();
        if (err.field === "titre" || err.field === "problemDescription") {
          setFieldErrors(prev => ({ ...prev, [err.field]: err.message }));
        }
        setCreateError(err.message || "Erreur lors de la création.");
        return;
      }
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setFieldErrors({});
      setTickets(null);
      loadTickets();
    } catch {
      setCreateError("Erreur réseau.");
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  }

  const formIsValid = Object.keys(
    validateTicketFields(form.titre, form.problemDescription),
  ).length === 0;

  const STATUS_FILTERS = [
    { value: "all", label: "Tous" },
    { value: "nouveau", label: "Nouveaux" },
    { value: "en_cours", label: "En cours" },
    { value: "en_attente_piece", label: "En attente" },
    { value: "resolu", label: "Résolus" },
    { value: "ferme", label: "Fermés" },
  ];

  const filtered = statusFilter === 'all' ? tickets : tickets?.filter(t => t.status === statusFilter);

  return (
    <div className="p-6 max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <LifeBuoy className="w-6 h-6" /> Tickets SAV
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Suivez vos demandes d'assistance et de réparation.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openShare}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            data-testid="button-share-ticket"
          >
            <Link2 className="w-4 h-4" /> Lien public
          </button>
          <button
            onClick={() => navigate("/robots")}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: primary }}
            data-testid="button-new-ticket"
          >
            <Bot className="w-4 h-4" /> Nouveau ticket
          </button>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative">
        {loading && tickets !== null ? (
          <Loader2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        ) : (
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        )}
        <input
          type="search"
          value={searchInput}
          onChange={(event) => {
            const value = event.target.value;
            setSearchInput(value);
            if (value.trim()) setStatusFilter("all");
          }}
          placeholder="Rechercher un ticket…"
          className="h-10 w-full rounded-lg border border-border bg-card pl-10 pr-10 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
          data-testid="input-ticket-search"
        />
        {searchInput.length > 0 && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label="Effacer la recherche"
            data-testid="button-clear-ticket-search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Filtres statut + bascule d'affichage */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              type="button"
              onClick={() => {
                if (searchInput) {
                  requestSequenceRef.current += 1;
                  setSearchInput("");
                  setSearchQuery("");
                  setLoading(false);
                  setError(null);
                  if (allTicketsRef.current) setTickets(allTicketsRef.current);
                }
                setStatusFilter(f.value);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${statusFilter === f.value ? 'text-white font-medium' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}
              style={statusFilter === f.value ? { backgroundColor: primary } : {}}
              data-testid={`filter-status-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div
          className="ml-auto flex shrink-0 items-center gap-0.5 rounded-lg border border-border bg-card p-0.5"
          aria-label="Mode d'affichage des tickets"
          data-testid="toggle-portail-tickets-view"
        >
          <button
            type="button"
            onClick={() => switchView("cards")}
            aria-pressed={viewMode === "cards"}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
              viewMode === "cards"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="button-portail-view-cards"
          >
            <LayoutGrid className="h-4 w-4" />
            <span className="hidden sm:inline">Cartes</span>
          </button>
          <button
            type="button"
            onClick={() => switchView("list")}
            aria-pressed={viewMode === "list"}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
              viewMode === "list"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="button-portail-view-list"
          >
            <List className="h-4 w-4" />
            <span className="hidden sm:inline">Liste</span>
          </button>
        </div>
      </div>

      {/* Résultats */}
      {loading && tickets === null && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-destructive text-sm p-4 bg-destructive/10 rounded-xl">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}
      {!error && filtered && filtered.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <LifeBuoy className="w-12 h-12 mx-auto text-muted-foreground/30" />
          {searchQuery ? (
            <>
              <p className="text-muted-foreground">
                Aucun ticket ne correspond à « {searchQuery} ».
              </p>
              <button
                type="button"
                onClick={clearSearch}
                className="text-sm font-medium hover:opacity-80 transition"
                style={{ color: primary }}
                data-testid="button-clear-empty-search"
              >
                Effacer la recherche
              </button>
            </>
          ) : (
            <>
              <p className="text-muted-foreground">Aucun ticket pour le moment.</p>
              <button
                onClick={() => navigate("/robots")}
                className="text-sm font-medium hover:opacity-80 transition"
                style={{ color: primary }}
              >
                Ouvrir un ticket depuis vos robots actifs
              </button>
            </>
          )}
        </div>
      )}
      {!error && filtered && filtered.length > 0 && (
        viewMode === "cards" ? (
          <div
            className={`grid gap-3 transition-opacity ${loading ? "opacity-60" : "opacity-100"}`}
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
            data-testid="grid-portail-ticket-cards"
          >
            {filtered.map(ticket => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => navigate(`/tickets/${ticket.id}`)}
                className="group w-full rounded-xl border border-border/50 bg-card p-4 text-left transition-all hover:border-border hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid={`ticket-card-${ticket.id}`}
              >
                <div className="flex h-full min-w-0 flex-col gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0 truncate font-mono text-xs text-muted-foreground">
                      {ticket.ticketNumber}
                    </span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[ticket.status] || "bg-secondary text-muted-foreground"}`}>
                      {STATUS_LABELS[ticket.status] || ticket.status}
                    </span>
                  </div>
                  <p
                    className="text-sm font-medium leading-relaxed text-foreground"
                    style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden" }}
                  >
                    {ticket.titre || ticket.ticketNumber}
                  </p>
                  <div className="mt-auto flex items-center justify-between gap-2 border-t border-border/50 pt-3 text-xs text-muted-foreground">
                    <span className="flex min-w-0 items-center gap-1">
                      <User className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{ticket.contactName || "Contact non renseigné"}</span>
                    </span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 font-medium ${PRIORITY_COLORS[ticket.priority] || "bg-secondary text-muted-foreground"}`}>
                      {PRIORITY_LABELS[ticket.priority] || ticket.priority}
                    </span>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5 shrink-0" />
                    {formatDate(ticket.createdAt)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div
            className={`overflow-hidden rounded-xl border border-border/60 bg-card transition-opacity ${loading ? "opacity-60" : "opacity-100"}`}
            data-testid="list-portail-tickets"
          >
            <div className="hidden grid-cols-[9rem_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto_7rem] items-center gap-3 border-b border-border/60 bg-secondary/40 px-3 py-2 text-xs font-medium text-muted-foreground sm:grid">
              <span>Référence</span>
              <span>Titre</span>
              <span>Contact</span>
              <span>Statut</span>
              <span>Priorité</span>
              <span>Date</span>
            </div>
            {filtered.map(ticket => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => navigate(`/tickets/${ticket.id}`)}
                className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-border/50 px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:grid-cols-[9rem_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto_7rem]"
                data-testid={`ticket-list-row-${ticket.id}`}
              >
                <span className="min-w-0">
                  <span className="block truncate font-mono text-xs text-muted-foreground">{ticket.ticketNumber}</span>
                  <span className="mt-0.5 block truncate text-sm font-medium text-foreground sm:hidden">
                    {ticket.titre || ticket.ticketNumber}
                  </span>
                </span>
                <span className="hidden min-w-0 truncate text-sm font-medium text-foreground sm:block">
                  {ticket.titre || ticket.ticketNumber}
                </span>
                <span className="hidden min-w-0 items-center gap-1 text-xs text-muted-foreground sm:flex">
                  <User className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{ticket.contactName || "—"}</span>
                </span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[ticket.status] || "bg-secondary text-muted-foreground"}`}>
                  {STATUS_LABELS[ticket.status] || ticket.status}
                </span>
                <span className={`hidden shrink-0 rounded-full px-2 py-0.5 text-xs font-medium sm:inline-flex ${PRIORITY_COLORS[ticket.priority] || "bg-secondary text-muted-foreground"}`}>
                  {PRIORITY_LABELS[ticket.priority] || ticket.priority}
                </span>
                <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex">
                  <Calendar className="h-3.5 w-3.5 shrink-0" />
                  {formatDate(ticket.createdAt)}
                </span>
              </button>
            ))}
          </div>
        )
      )}

      {/* Modal création ticket */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => { setShowCreate(false); setTicketRobotContext(null); setForm(EMPTY_FORM); setCreateError(null); setFieldErrors({}); }}>
          <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-border/60">
              <h2 className="text-lg font-semibold text-foreground">Nouveau ticket SAV</h2>
              <p className="text-sm text-muted-foreground mt-0.5">Décrivez votre problème, nous vous répondrons rapidement.</p>
              {ticketRobotContext && (
                <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-sm">
                  <Bot className="w-4 h-4 shrink-0" />
                  <span>Robot&nbsp;<span className="font-mono font-semibold">{ticketRobotContext.sn}</span>{ticketRobotContext.modele ? ` — ${ticketRobotContext.modele}` : ""} pré-sélectionné depuis vos robots actifs.</span>
                </div>
              )}
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-5">
              <div className="space-y-1.5">
                 <label className="text-sm font-medium text-foreground">Objet *</label>
                <input
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                   placeholder={PORTAIL_TICKET_TITLE_PLACEHOLDER}
                  value={form.titre}
                   onChange={e => {
                     setForm({ ...form, titre: e.target.value });
                     setFieldErrors(prev => ({ ...prev, titre: undefined }));
                   }}
                   aria-invalid={!!fieldErrors.titre}
                  data-testid="input-ticket-titre"
                />
                 {fieldErrors.titre && <p className="text-sm text-destructive">{fieldErrors.titre}</p>}
              </div>
              <div className="space-y-1.5">
                 <label className="text-sm font-medium text-foreground">Description du problème *</label>
                <textarea
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  rows={3}
                  placeholder="Décrivez le problème en détail..."
                  value={form.problemDescription}
                   onChange={e => {
                     setForm({ ...form, problemDescription: e.target.value });
                     setFieldErrors(prev => ({ ...prev, problemDescription: undefined }));
                   }}
                   aria-invalid={!!fieldErrors.problemDescription}
                  data-testid="input-ticket-description"
                />
                 {fieldErrors.problemDescription && (
                   <p className="text-sm text-destructive">{fieldErrors.problemDescription}</p>
                 )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Nom du client final</label>
                <input
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Le magasin ou établissement où le robot est installé"
                  value={form.clientFinalNom}
                  onChange={e => setForm({ ...form, clientFinalNom: e.target.value })}
                  data-testid="input-ticket-client-final"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Numéro de série</label>
                  <input
                    className={`w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${ticketRobotContext ? "border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20 text-blue-800 dark:text-blue-200 font-mono cursor-not-allowed" : "border-border"}`}
                    placeholder="Ex : T600-XXXX"
                    value={form.serialNumberFree}
                    onChange={e => !ticketRobotContext && setForm({ ...form, serialNumberFree: e.target.value })}
                    readOnly={!!ticketRobotContext}
                    data-testid="input-ticket-serial"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Date de panne</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={form.datePanne}
                    onChange={e => setForm({ ...form, datePanne: e.target.value })}
                    data-testid="input-ticket-date-panne"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Nom du contact</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Prénom Nom"
                    value={form.contactName}
                    onChange={e => setForm({ ...form, contactName: e.target.value })}
                    data-testid="input-ticket-contact-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Email contact</label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="contact@exemple.com"
                    value={form.contactEmail}
                    onChange={e => setForm({ ...form, contactEmail: e.target.value })}
                    data-testid="input-ticket-contact-email"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Téléphone contact</label>
                <input
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="+33 6 XX XX XX XX"
                  value={form.contactPhone}
                  onChange={e => setForm({ ...form, contactPhone: e.target.value })}
                  data-testid="input-ticket-contact-phone"
                />
              </div>

              <div className="border-t border-border/60 pt-4 space-y-3">
                <p className="text-sm font-medium text-foreground">Livraison (optionnel)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Nom entreprise</label>
                    <input
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={form.livraisonNomEntreprise}
                      onChange={e => setForm({ ...form, livraisonNomEntreprise: e.target.value })}
                      data-testid="input-livraison-entreprise"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Adresse</label>
                    <input
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={form.livraisonAdresse}
                      onChange={e => setForm({ ...form, livraisonAdresse: e.target.value })}
                      data-testid="input-livraison-adresse"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Contact livraison</label>
                    <input
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={form.livraisonContact}
                      onChange={e => setForm({ ...form, livraisonContact: e.target.value })}
                      data-testid="input-livraison-contact"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Téléphone livraison</label>
                    <input
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={form.livraisonPhone}
                      onChange={e => setForm({ ...form, livraisonPhone: e.target.value })}
                      data-testid="input-livraison-phone"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Vidéo du problème (optionnel, max 50 Mo)</label>
                <input
                  type="file"
                  accept="video/*"
                  className="w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-border file:text-xs file:bg-secondary file:text-foreground hover:file:bg-secondary/80 cursor-pointer"
                  onChange={e => setForm({ ...form, video: e.target.files?.[0] || null })}
                  data-testid="input-ticket-video"
                />
              </div>

              {createError && (
                <p className="text-sm text-destructive">{createError}</p>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowCreate(false); setCreateError(null); setFieldErrors({}); setForm(EMPTY_FORM); setTicketRobotContext(null); }}
                  className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
                  data-testid="button-cancel-create-ticket"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                   disabled={submitting || !formIsValid}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 transition-colors hover:opacity-90"
                  style={{ backgroundColor: primary }}
                  data-testid="button-submit-ticket"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Envoyer le ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal lien public */}
      {showShare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setShowShare(false)}>
          <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-border/60">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Link2 className="w-5 h-5" /> Lien de dépôt public
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Partagez ce lien à vos clients pour qu'ils puissent déposer un ticket SAV sans créer de compte.
              </p>
            </div>
            <div className="p-6 space-y-4">
              {loadingToken && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {!loadingToken && shareToken && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={getPublicUrl()}
                      className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-xs text-muted-foreground font-mono"
                      data-testid="input-share-url"
                    />
                    <button
                      onClick={copyUrl}
                      className="p-2 rounded-lg border border-border hover:bg-secondary/60 transition-colors"
                      data-testid="button-copy-url"
                      title="Copier le lien"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
                    </button>
                  </div>
                  <button
                    onClick={regenerateToken}
                    disabled={loadingToken}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="button-regenerate-token"
                  >
                    <RefreshCw className="w-3 h-3" /> Regénérer le lien
                  </button>
                </div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowShare(false)}
                  className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
                  data-testid="button-close-share"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
